import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Play, Plus, Check, Radio, Gamepad2, Tv } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export const ContentCard = ({ item, wide = false }) => {
  const nav = useNavigate();
  const { user, isFavorite, toggleFavorite } = useAuth();
  const [imgError, setImgError] = useState(false);
  const fav = isFavorite(item.id);
  const isLive = item.type === "live";
  const isGame = item.type === "game";
  const isRadio = item.type === "radio";
  const isSeries = item.type === "series";
  const logoStyle = isLive || isRadio;
  const showFallback = !item.poster || imgError;

  const onFav = async (e) => {
    e.stopPropagation();
    if (!user) { toast.error("Sign in to save to your list"); nav("/login"); return; }
    const added = await toggleFavorite(item.id);
    toast.success(added ? "Added to My List" : "Removed from My List");
  };

  return (
    <div
      data-testid={`content-card-${item.id}`}
      tabIndex={0}
      onClick={() => nav(`/watch/${item.id}`)}
      className={`group relative shrink-0 cursor-pointer rounded-xl overflow-hidden bg-card border border-white/5
        transition-all duration-300 hover:scale-[1.06] hover:z-20 hover:border-brand/50 hover:shadow-[0_12px_40px_rgba(229,9,20,0.35)]
        ${wide ? "w-[260px] aspect-video" : "w-[160px] sm:w-[180px] aspect-[2/3]"}`}
    >
      {!showFallback ? (
        <img src={item.poster} alt={item.title} loading="lazy"
          onError={() => setImgError(true)}
          className={`w-full h-full ${logoStyle ? "object-contain p-6 bg-gradient-to-br from-zinc-800 to-zinc-900" : "object-cover"}`} />
      ) : (
        <div className={`w-full h-full grid place-items-center p-4 text-center bg-gradient-to-br ${
          isRadio ? "from-sky-900/60 via-zinc-900 to-black"
          : isSeries ? "from-violet-900/60 via-zinc-900 to-black"
          : isLive ? "from-brand/30 via-zinc-900 to-black"
          : "from-pink/30 via-zinc-900 to-black"}`}>
          <span className={`w-14 h-14 mb-3 grid place-items-center rounded-full ${
            isRadio ? "bg-sky-500/20 text-sky-400"
            : isSeries ? "bg-violet-500/20 text-violet-300"
            : isLive ? "bg-brand/25 text-brand"
            : "bg-pink/20 text-pink"}`}>
            {isRadio || isLive ? <Radio size={26} /> : isSeries ? <Tv size={26} /> : <Gamepad2 size={26} />}
          </span>
          <span className="text-sm font-bold line-clamp-3 leading-tight">{item.title}</span>
          <span className="text-[10px] uppercase tracking-widest mt-1 text-zinc-500">
            {isRadio ? "Live Radio" : isSeries ? "Series" : isLive ? "Live TV" : "Game"}
          </span>
          <span className="text-[9px] font-extrabold tracking-wide mt-2 text-zinc-600">NeTime<span className="text-brand">TV</span></span>
        </div>
      )}

      {/* badges */}
      <div className="absolute top-2 left-2 flex gap-1.5">
        {isLive ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-brand text-white animate-pulse">
            <span className="w-1.5 h-1.5 rounded-full bg-white" /> LIVE
          </span>
        ) : isGame ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-pink text-white"><Gamepad2 size={10} /> GAME</span>
        ) : isRadio ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-sky-500 text-white"><Radio size={10} /> RADIO</span>
        ) : isSeries ? (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-violet-600 text-white"><Tv size={10} /> SERIES</span>
        ) : null}
      </div>
      {item.quality && (
        <div className="absolute top-2 right-2">
          <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-black/70 backdrop-blur text-white border border-white/20">{item.quality}</span>
        </div>
      )}

      {/* live EPG strip (now playing) */}
      {isLive && item.epg && (
        <div data-testid="epg-strip" className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black via-black/70 to-transparent group-hover:opacity-0 transition-opacity">
          <p className="text-[10px] text-zinc-200 truncate font-medium">● {item.epg.now.title}</p>
          <div className="h-1 bg-white/20 rounded-full mt-1 overflow-hidden">
            <div className="h-full bg-brand rounded-full" style={{ width: `${item.epg.now.progress}%` }} />
          </div>
        </div>
      )}

      {isGame && (
        <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black via-black/70 to-transparent group-hover:opacity-0 transition-opacity">
          <p className="text-[11px] text-white font-semibold truncate">{item.title}</p>
        </div>
      )}

      {/* hover overlay */}
      <div className="card-actions absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-3">
        <p className="font-semibold text-sm truncate">{item.title}</p>
        {isLive && item.epg ? (
          <p className="text-[11px] text-zinc-300 truncate mb-2">
            <span className="text-brand font-semibold">{item.epg.now.start}</span> {item.epg.now.title} · Next: {item.epg.next.title}
          </p>
        ) : (
          <p className="text-[11px] text-zinc-400 truncate mb-2">
            {item.category_label}{item.year ? ` · ${item.year}` : ""}{item.country ? ` · ${item.country}` : ""}
          </p>
        )}
        <div className="flex items-center gap-2">
          <button data-testid={`play-${item.id}`}
            className="flex-1 flex items-center justify-center gap-1 bg-white text-black text-xs font-bold py-1.5 rounded-md hover:bg-brand hover:text-white transition-colors">
            <Play size={13} fill="currentColor" /> {isLive ? "Watch" : isRadio ? "Listen" : "Play"}
          </button>
          <button data-testid={`fav-${item.id}`} onClick={onFav}
            className="w-8 h-8 grid place-items-center rounded-md border border-white/30 hover:border-white bg-black/50 transition-colors">
            {fav ? <Check size={14} className="text-brand" /> : <Plus size={14} />}
          </button>
        </div>
      </div>
    </div>
  );
};
